import { failure, success } from '../types.js';
import type { Cents, Fraction, Result } from '../types.js';

/** Money and counts remain JSON-compatible numbers; intermediate products are exact. */
export type Ratio = Fraction;

export type Rounding = 'floor' | 'nearest';

export function asCents(value: number): Result<Cents> {
  return Number.isSafeInteger(value) ? success(value as Cents) : failure('invalid_cents');
}

/** Programmer assertion for already-validated arithmetic, including negative balances. */
export function cents(value: number): Cents {
  if (!Number.isSafeInteger(value)) throw new RangeError('Unsafe integer cents.');
  return value as Cents;
}

export function fraction(num: number, den: number): Result<Fraction> {
  if (!Number.isSafeInteger(num) || !Number.isSafeInteger(den) || num < 0 || den <= 0 || num > den)
    return failure('invalid_fraction');
  let a = BigInt(num);
  let b = BigInt(den);
  while (b !== 0n) {
    const rest = a % b;
    a = b;
    b = rest;
  }
  return success({ num: Number(BigInt(num) / a), den: Number(BigInt(den) / a) });
}

export function floorDiv(numerator: number, denominator: number): number {
  assertAmount(numerator);
  assertCount(denominator);
  if (denominator === 0) throw new RangeError('Denominator must be positive.');
  return Number(BigInt(numerator) / BigInt(denominator));
}

export function roundHalfUpToCent(value: number, ratio: Fraction): Cents {
  return cents(multiplyRatio(value, ratio, 'nearest'));
}

export function assertAmount(value: number): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError('Amount must be a nonnegative safe integer in cents.');
  }
  return value;
}

export function assertCount(value: number): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError('Count must be a nonnegative safe integer.');
  }
  return value;
}

export function assertRatio(ratio: Ratio): Ratio {
  assertCount(ratio.num);
  assertCount(ratio.den);
  if (ratio.den === 0 || ratio.num > ratio.den) {
    throw new RangeError('Ratio must be between zero and one with a positive denominator.');
  }
  return { num: ratio.num, den: ratio.den };
}

export function multiplyRatio(value: number, ratio: Ratio, rounding: Rounding): number {
  assertAmount(value);
  assertRatio(ratio);
  const product = BigInt(value) * BigInt(ratio.num);
  const denominator = BigInt(ratio.den);
  const quotient = product / denominator;
  const roundUp = rounding === 'nearest' && (product % denominator) * 2n >= denominator;
  return Number(quotient + (roundUp ? 1n : 0n));
}

export function sumAmounts(values: readonly number[]): number {
  const total = values.reduce((sum, value) => sum + BigInt(assertAmount(value)), 0n);
  if (total > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new RangeError('Total exceeds safe integer cents.');
  }
  return Number(total);
}
